/**
 * Triangle Background Demo - Main Entry
 */

import { BackgroundSystem, type AnimationType } from '@trinity/background-system';

// DOM Elements
const canvas = document.getElementById('canvas') as HTMLCanvasElement;
const deviceFrame = document.getElementById('device-frame') as HTMLDivElement;
const animationSelect = document.getElementById('animation-select') as HTMLSelectElement;
const speedSlider = document.getElementById('speed-slider') as HTMLInputElement;
const intensitySlider = document.getElementById('intensity-slider') as HTMLInputElement;
const sizeSlider = document.getElementById('size-slider') as HTMLInputElement;
const speedValue = document.getElementById('speed-value') as HTMLSpanElement;
const intensityValue = document.getElementById('intensity-value') as HTMLSpanElement;
const sizeValue = document.getElementById('size-value') as HTMLSpanElement;
const fpsDisplay = document.getElementById('fps-display') as HTMLSpanElement;
const triangleCountDisplay = document.getElementById('triangle-count') as HTMLSpanElement;
const deviceNameDisplay = document.getElementById('device-name') as HTMLDivElement;
const deviceDimensionsDisplay = document.getElementById('device-dimensions') as HTMLDivElement;
const filterSelect = document.getElementById('filter-select') as HTMLSelectElement;
const deviceButtons = document.querySelectorAll('.device-btn') as NodeListOf<HTMLButtonElement>;

// Device presets with dimensions
const devicePresets: Record<string, { name: string; width: number; height: number }> = {
  'fullscreen': { name: 'Fullscreen', width: 0, height: 0 },
  'laptop': { name: 'Laptop', width: 1280, height: 800 },
  'tablet': { name: 'iPad Pro', width: 820, height: 1180 },
  'tablet-landscape': { name: 'iPad Landscape', width: 1180, height: 820 },
  'mobile': { name: 'iPhone 14 Pro', width: 390, height: 844 },
  'mobile-landscape': { name: 'iPhone Landscape', width: 844, height: 390 },
};

let currentDevice = 'fullscreen';

// Initialize background system
const bg = new BackgroundSystem(canvas, {
  sideLength: 35,
  animation: 'swarm',
  animationConfig: {
    speed: 1,
    intensity: 0.6,
    noiseScale: 2.5,
    mouseRadius: 0.12,
    mouseStrength: 0.9,
  },
});

// Device switching
function setDevice(device: string) {
  currentDevice = device;

  // Update frame class
  deviceFrame.className = `device-frame ${device}`;

  // Update button states
  deviceButtons.forEach((btn) => {
    btn.classList.toggle('active', btn.dataset.device === device);
  });

  // Update indicator
  const preset = devicePresets[device];
  if (deviceNameDisplay && preset) {
    deviceNameDisplay.textContent = preset.name;
  }

  // Update dimensions after transition
  setTimeout(updateDimensions, 50);

  // Trigger resize for background system
  setTimeout(() => {
    // Force the background system to recalculate
    const container = canvas.parentElement;
    if (container) {
      const rect = container.getBoundingClientRect();
      // The BackgroundSystem handles resize via ResizeObserver
    }
  }, 400);
}

function updateDimensions() {
  const container = canvas.parentElement;
  if (container && deviceDimensionsDisplay) {
    const rect = container.getBoundingClientRect();
    const w = Math.round(rect.width);
    const h = Math.round(rect.height);
    deviceDimensionsDisplay.textContent = `${w} × ${h}`;
  }
}

// Device button handlers
deviceButtons.forEach((btn) => {
  btn.addEventListener('click', () => {
    const device = btn.dataset.device;
    if (device) {
      setDevice(device);
    }
  });
});

// Animation select
animationSelect?.addEventListener('change', () => {
  const val = animationSelect.value;
  if (val.startsWith('ivm:')) {
    const presetId = val.slice(4);
    bg.setConfig({ animation: 'ivm', ivmPreset: presetId });
  } else {
    bg.setAnimation(val as AnimationType | 'cascade');
  }
});

// Filter select
filterSelect?.addEventListener('change', () => {
  const val = filterSelect.value;
  bg.setConfig({ ivmFilter: val || undefined });
});

// Speed slider
speedSlider?.addEventListener('input', () => {
  const speed = parseFloat(speedSlider.value);
  speedValue.textContent = speed.toFixed(1);
  bg.setConfig({
    animationConfig: { speed },
  });
});

// Intensity slider
intensitySlider?.addEventListener('input', () => {
  const intensity = parseFloat(intensitySlider.value);
  intensityValue.textContent = intensity.toFixed(1);
  bg.setConfig({
    animationConfig: { intensity },
  });
});

// Size slider
sizeSlider?.addEventListener('input', () => {
  const size = parseInt(sizeSlider.value, 10);
  sizeValue.textContent = `${size}px`;
  bg.setConfig({ sideLength: size });
});

// Update stats
function updateStats() {
  if (fpsDisplay) fpsDisplay.textContent = String(bg.getFPS());
  if (triangleCountDisplay) triangleCountDisplay.textContent = String(bg.getTriangleCount());
}

setInterval(updateStats, 500);

// Keyboard shortcuts
document.addEventListener('keydown', (e) => {
  const animations: (AnimationType | 'cascade')[] = ['swarm', 'wave', 'pulse', 'vortex', 'breathe', 'flow', 'cascade'];
  const devices = ['fullscreen', 'laptop', 'tablet', 'tablet-landscape', 'mobile', 'mobile-landscape'];

  // 1-7 for animations
  if (e.key >= '1' && e.key <= '7') {
    const idx = parseInt(e.key, 10) - 1;
    if (idx < animations.length) {
      bg.setAnimation(animations[idx] as AnimationType);
      animationSelect.value = animations[idx];
    }
  }

  // D to cycle devices
  if (e.key.toLowerCase() === 'd') {
    const currentIdx = devices.indexOf(currentDevice);
    const nextIdx = (currentIdx + 1) % devices.length;
    setDevice(devices[nextIdx]);
  }

  // F for fullscreen
  if (e.key.toLowerCase() === 'f' && !e.ctrlKey && !e.metaKey) {
    setDevice('fullscreen');
  }

  // M for mobile
  if (e.key.toLowerCase() === 'm') {
    setDevice(currentDevice === 'mobile' ? 'mobile-landscape' : 'mobile');
  }

  // T for tablet
  if (e.key.toLowerCase() === 't') {
    setDevice(currentDevice === 'tablet' ? 'tablet-landscape' : 'tablet');
  }

  // L for laptop
  if (e.key.toLowerCase() === 'l') {
    setDevice('laptop');
  }

  // Space to pause/resume
  if (e.key === ' ') {
    e.preventDefault();
    bg.start();
  }

  // Escape to go back to playground
  if (e.key === 'Escape') {
    window.location.href = '/playground.html';
  }
});

// Window resize handler
window.addEventListener('resize', () => {
  updateDimensions();
});

// Initial dimension update
setTimeout(updateDimensions, 100);

// Log
console.log('🔺 Trinity Triangle Background');
console.log('Animations: 1-6 | Devices: D=cycle, F=full, M=mobile, T=tablet, L=laptop');
console.log('Move mouse to interact');
